import './EmailCode.css'

import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import { Button } from '#design-system/component/Button/Button'
import { Input } from '#design-system/component/Form/Input/Input'
import { Banner } from '#design-system/component/Layout/Banner/Banner'
import { useAuth } from '~/common/hooks/useAuth'
import { getAuthRoute } from '~/common/utils/auth-routes'

import { AuthLayout } from './AuthLayout'
import { OTPInput } from './OTPInput'

const CODE_LENGTH = 6

/** Seconds before another code can be requested. */
const RESEND_DELAY_SECONDS = 90

export const EmailCode = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { signInWithCode, isSigningIn } = useAuth()

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)

  // Set once a code is on its way, which swaps the email field for the code entry.
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [secondsUntilResend, setSecondsUntilResend] = useState(0)

  // Verifying disables the code field, which drops focus. Bumping this on a rejected code
  // remounts it, so the first digit takes focus again.
  const [attempt, setAttempt] = useState(0)

  // Tick the resend countdown down to zero.
  useEffect(() => {
    if (secondsUntilResend <= 0) {
      return undefined
    }
    const timer = setTimeout(() => {
      setSecondsUntilResend((seconds) => seconds - 1)
    }, 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [secondsUntilResend])

  const sendCode = async (): Promise<void> => {
    setError(null)
    setIsSending(true)

    try {
      const response = await fetch(getAuthRoute('magic-auth/send-code'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string }
        throw new Error(data.message ?? t('auth.send_code_failed'))
      }

      setSentTo(email)
      setCode('')
      setSecondsUntilResend(RESEND_DELAY_SECONDS)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.send_code_failed'))
    } finally {
      setIsSending(false)
    }
  }

  const verifyCode = async (enteredCode: string): Promise<void> => {
    if (sentTo === null) {
      return
    }
    setError(null)

    try {
      await signInWithCode(sentTo, enteredCode)
      void navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.invalid_code'))
      setCode('')
      setAttempt((count) => count + 1)
    }
  }

  const handleCodeChange = (next: string): void => {
    setCode(next)
    // Retyping is the user answering the error, so it stops being current.
    setError(null)
    // No submit button in this step, so the last digit completes the code.
    if (next.length === CODE_LENGTH) {
      void verifyCode(next)
    }
  }

  if (sentTo !== null) {
    return (
      <AuthLayout
        subtitle={t('auth.code_sent_body', { email: sentTo })}
        title={t('auth.check_your_email')}
        type="email-code"
      >
        {error !== null && (
          <Banner bodyText={error} headerText={t('auth.error')} semanticType="Error" />
        )}

        <p className="note">{t('auth.check_spam_folder')}</p>

        <div className="code-entry">
          <OTPInput
            key={attempt}
            autoFocus
            disabledMessage={isSigningIn ? t('auth.verifying') : undefined}
            length={CODE_LENGTH}
            onChange={handleCodeChange}
            value={code}
          />

          <p className="resend">
            {secondsUntilResend > 0 ? (
              t('auth.resend_code_in', { seconds: secondsUntilResend })
            ) : (
              <button disabled={isSending} onClick={() => void sendCode()} type="button">
                {t('auth.resend_code')}
              </button>
            )}
          </p>

          <div className="change-email">
            <button
              onClick={() => {
                setSentTo(null)
                setError(null)
                setCode('')
              }}
              type="button"
            >
              {t('auth.try_different_email')}
            </button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      subtitle={t('auth.email_code_subtitle')}
      title={t('auth.email_me_a_code')}
      type="email-code"
    >
      {error !== null && (
        <Banner bodyText={error} headerText={t('auth.error')} semanticType="Error" />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void sendCode()
        }}
      >
        <Input
          autoComplete="email"
          label={t('auth.email')}
          onChange={setEmail}
          onLiveChange={setEmail}
          placeholder={t('auth.email_placeholder')}
          required
          size="full"
          type="email"
          value={email}
        />

        <Button
          actionRole="primary"
          disabledMessage={isSending ? t('auth.sending') : undefined}
          label={t('auth.send_code')}
          type="submit"
        />
      </form>

      <div className="back-link">
        <Link to="/auth/login">{t('auth.back_to_login')}</Link>
      </div>
    </AuthLayout>
  )
}
